import { useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * Stops the user from leaving the page while `when` is true and hands the blocked navigation to
 * `onBlock`, which can call `retry()` once they confirm. Also raises the browser's own prompt for
 * tab close / reload, since those never reach the router.
 *
 * react-router 6.0 has no blocker hook, so this uses the history object's `block` directly.
 */
const useLeaveGuard = (when: boolean, onBlock: (retry: () => void) => void) => {
  const { navigator } = useContext(NavigationContext) as any;
  const onBlockReference = useRef(onBlock);
  onBlockReference.current = onBlock;

  useEffect(() => {
    let unblock: (() => void) | undefined;
    if (when && typeof navigator?.block === 'function') {
      unblock = navigator.block((transition: any) => {
        onBlockReference.current(() => {
          unblock?.();
          transition.retry();
        });
      });
    }
    return () => unblock?.();
  }, [when, navigator]);

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Some browsers still require a returnValue to show the prompt.
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };
    if (when) window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when]);
};

export default useLeaveGuard;
